import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollAnimatedSection } from './ScrollAnimatedSection';
import { Button } from './Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';
import { DraggableProductImage } from './DraggableProductImage';
import { MobileOptimizedProductImage } from './MobileOptimizedProductImage';
import { useShopifyPrice } from '../hooks/useShopifyPrice';
import { Gift } from 'lucide-react';

export function ProductHighlight({ product, reverse = false }) {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const navigate = useNavigate();
  
  // Fetch price from Shopify
  const { price, variantId, available, loading } = useShopifyPrice(product.id);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Use Shopify price if available, otherwise fallback to local price
  const displayPrice = price || product.price;
  
  // Adjust image size based on product - Rush and Synaptiq should be smaller
  const imageSize = (product.id === 'rush' || product.id === 'synaptiq') 
    ? 'max-w-xs md:max-w-sm' 
    : 'max-w-sm md:max-w-md';

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <div className={`grid md:grid-cols-2 gap-12 items-center ${reverse ? 'md:flex-row-reverse' : ''}`}>
          <ScrollAnimatedSection 
            direction={reverse ? "right" : "left"}
            className={reverse ? 'md:order-2' : ''}
          >
            <div className="relative">
              {product.images ? (
                isTouchDevice ? (
                  <MobileOptimizedProductImage 
                    images={product.images} 
                    title={product.title}
                    className={`${imageSize} mx-auto`}
                  />
                ) : (
                  <DraggableProductImage 
                    images={product.images} 
                    title={product.title}
                    className={`${imageSize} mx-auto`}
                  />
                )
              ) : (
                <img
                  src={product.image}
                  alt={product.title}
                  className={`w-full ${imageSize} mx-auto drop-shadow-xl`}
                />
              )}
              <div className="absolute inset-0 bg-berleto-sand/20 rounded-full blur-3xl transform scale-75 -z-10" />
            </div>
          </ScrollAnimatedSection>
          <ScrollAnimatedSection 
            direction={reverse ? "left" : "right"}
            delay={0.2}
            className={reverse ? 'md:order-1' : ''}
          >
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-light text-berleto-warmgray">
                {product.title}
              </h2>
              <p className="text-lg text-berleto-sage leading-relaxed">
                {product.description}
              </p>

              <Tabs defaultValue="ingredients" className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 gap-1">
                  <TabsTrigger value="ingredients" className="text-xs md:text-sm">Ingredients</TabsTrigger>
                  <TabsTrigger value="benefits" className="text-xs md:text-sm">Benefits</TabsTrigger>
                  <TabsTrigger value="usage" className="text-xs md:text-sm col-span-2 md:col-span-1">Usage</TabsTrigger>
                </TabsList>
                <TabsContent value="ingredients" className="mt-4">
                  <div className="space-y-2 text-sm text-berleto-sage">
                    {(product.ingredientsSimple || product.ingredients)?.map((ingredient, index) => (
                      <p key={index}>• {typeof ingredient === 'string' ? ingredient : `${ingredient.name} (${ingredient.amount})`}</p>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="benefits" className="mt-4">
                  <div className="space-y-2 text-sm text-berleto-sage">
                    {product.benefits?.map((benefit, index) => (
                      <p key={index}>• {benefit}</p>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="usage" className="mt-4">
                  <p className="text-sm text-berleto-sage">
                    {product.usage || "Take 2 capsules daily with water, preferably with a meal."}
                  </p>
                </TabsContent>
              </Tabs>

              <div className="space-y-3 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {/* Add to Cart — primary */}
                  <Button
                    size="lg"
                    className="font-medium tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#243424'}
                    onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
                    onClick={() => product.onAddToCart(variantId)}
                    disabled={!available || loading}
                  >
                    {loading ? 'Loading…' : !available ? 'Out of Stock' : (
                      <span className="flex items-center gap-2">
                        Add to Cart
                        <span style={{ color: '#B8704D', fontWeight: 500 }}>
                          ${displayPrice.toFixed(2)}
                        </span>
                      </span>
                    )}
                  </Button>

                  {/* Product Details — secondary */}
                  <Button
                    size="lg"
                    variant="outline"
                    className="font-medium transition-colors"
                    style={{ border: '1px solid rgba(26,42,26,0.2)', color: '#1A2A1A', background: 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#E8DCC4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    Details
                  </Button>

                  {/* Bundle — desktop */}
                  <Button
                    size="lg"
                    className="hidden md:flex items-center justify-center gap-2 font-medium transition-colors text-white"
                    style={{ background: '#B8704D' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#a56340'}
                    onMouseLeave={e => e.currentTarget.style.background = '#B8704D'}
                    onClick={() => navigate('/bundle')}
                  >
                    <Gift className="w-4 h-4" />
                    Bundle
                  </Button>
                </div>

                {/* Bundle — mobile full width */}
                <Button
                  size="lg"
                  className="md:hidden w-full flex items-center justify-center gap-2 font-medium text-white transition-colors"
                  style={{ background: '#B8704D' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#a56340'}
                  onMouseLeave={e => e.currentTarget.style.background = '#B8704D'}
                  onClick={() => navigate('/bundle')}
                >
                  <Gift className="w-4 h-4" />
                  Bundle & Save
                </Button>
              </div>
            </div>
          </ScrollAnimatedSection>
        </div>
      </div>
    </section>
  );
}